import { useEffect, useMemo, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { ReactComponent as Logo } from '../../assets/svg/LogoHeader.svg';
import { useBranding } from '../../context/brandingContext';
import {
  NavMenuContainer,
  NavMenuCon,
  NavMenuMobileContainer,
  MobileMenuBackdrop,
  ProfileDropdownContainer,
  ProfileDrop,
  ProfileDropdownMenu,
  DropDownMenuSuperAdmin,
  DropDownMenuAdmin,
  MobileNavItem,
  MobileSubMenu,
  ProfileDropWrapper,
  HeaderNotice,
  NavbarContainerMain,
  Spacer,
} from './navbar.styled';
import { useDispatch, useSelector } from 'react-redux';
import { selectAuthRoutesList } from '../../pages/configuration/routeSlice';
import AuthService from '../../services/authService';
import CheckPermissions from '../../utils/permission/checkPermission';
import {
  EquityAwardsList,
  MyTaskRouteList,
  TransactionsList,
  ReportList,
  TaskManagementConsole,
} from '../../constants/innerRoutesList';
import { getUserName, NavPermissionCheck } from '../../utils/global';
import { selectToken } from '../../pages/auth/authSlice';
import Heading from '../typography';
import Notification from './notification';
import AnimationWrapper from '../animation/animationWrapper';
import NotificationModal from './notification/notificationModal';
import { setNotify } from '../../pages/auth/notify';
import Button from '../button';
// eslint-disable-next-line camelcase
import { type Wealthlane_NotificationService_DTOs_BellNotificationDto } from '../../services/wealthlane-notification-services';
import useWealthlaneCustomerApi from '../../hooks/useWealthlaneCustomerApi';
import useGetCustomerUserDetails from '../../hooks/get/useGetCustomerUserDetails';
import useWealthlaneNotificationApi from '../../hooks/useWealthlaneNotificationApi';
import useBoolean from '../../hooks/custom-hooks/useBoolean';
import useGetUnseenNotificationCount from '../../hooks/get/useGetUnseenNotificationCount';
import { ChatbotWithContext } from '@wealthlane/chatbot';
import {
  useGetCompanyProfileDetails,
  useGetCurrentTenantInfo,
  useWealthlaneAwardApi,
} from '../../hooks';
import { NavLogo } from './navLogo';
import { selectIsOrigin } from '../../pages/configuration/customerSwitchUserSlice';
import SwitchSandboxModal from '../../pages/settings/sandbox/switchSandboxModal';
import InitialValuesForm from '../../constants/initialValues';
import NavbarSwitchModal from '../../pages/navbar-modal';
import useFilePicker from '../../hooks/custom-hooks/useFilePicker';
import usePermissionCheck from '../../hooks/custom-hooks/usePermissionCheck';
import useInstance from '../../hooks/custom-hooks/useInstance';
import { createSignalRConnection } from '../../utils/signalRClient';
import { Portal } from '../vertical-dot/popperContainer';
import { createPortal } from 'react-dom';
import { axiosInstance } from '../../services/apiInterceptor';
import { configState } from '../ingest';
import useGetCurrentTenantInfoEnabled from '../../hooks/get/useGetCurrentTenantInfoEnabled';
import ToastComponent from '../toast';
import { toast } from 'react-toastify';

interface NavbarType {
  isAuth: boolean;
  showNotify: boolean;
  isNotify: boolean;
}

const Navbar = ({ isAuth, showNotify, isNotify }: NavbarType): JSX.Element => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { appLogoUrl, isEnabled: isBrandingEnabled, isResolved: isBrandingResolved } = useBranding();
  
  // Check if we're in an authentication flow where branding is not available
  const isAuthenticatingFlow = useMemo(() => {
    const pathname = location.pathname.toLowerCase();
    const search = location.search.toLowerCase();
    
    // Callback routes, impersonation, or any auth-related loading states
    return (
      pathname.includes('/callback') ||
      pathname.includes('/silent-renew') ||
      pathname.includes('/signin') ||
      search.includes('impersonatetoken') ||
      search.includes('code=') // OAuth callback with code
    );
  }, [location.pathname, location.search]);
  // Force showNotify to always be false to disable the header notice
  // (ignore incoming prop to preserve caller contracts)
  const showNotifyFalse = false;
  const token = useSelector(selectToken);
  // const isOrigin = useSelector(selectIsOrigin);
  const userAuthList = useSelector(selectAuthRoutesList);
  const authService = new AuthService();

  // Helper function to convert strings to camelCase
  const toCamelCase = (str: string): string => {
    return str
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
      .split(/\s+/) // Split by spaces
      .map((word, index) => {
        if (index === 0) {
          return word.toLowerCase();
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join('');
  };
  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const {
    value: isSandboxModalOpen,
    setFalse: closeSandboxModal,
    setTrue: openSandboxModal,
  } = useBoolean(false);
  const {
    value: isSwitchModalOpen,
    setFalse: closeSwitchModal,
    setTrue: openSwitchModal,
  } = useBoolean(false);
  const {
    value: isChatModalOpen,
    setFalse: closeChatModal,
    setTrue: openChatModal,
  } = useBoolean(false);

  const { handleRequest } = useWealthlaneNotificationApi();
  const { handleRequest: awardHandleRequest } = useWealthlaneAwardApi();

  const { data: NotificationCount, refetch } = useGetUnseenNotificationCount(handleRequest, token);
  const { data: currentTenant, isLoading: isCurrentTenantLoading } =
    useGetCurrentTenantInfo(handleRequest, isAuth);
  const { isSwitched, getRoleId, getCurrentCid } = useInstance();
  const cid = getCurrentCid();

  const [sourceRoleId, _] = useState(getRoleId())
  console.log(sourceRoleId, "SOurce ROLE ID ",)

  const [openSideNav, setOpenSideNav] = useState(false);
  const [open, setOpen] = useState(false);
  // const [subMenu, setSubMenu] = useState(false);
  const [isLoggedIn, setLoggedIn] = useState(true);
  const [toggleAwards, setToggleAwards] = useState(false);
  const [toggleBrokerage, setToggleBrokerage] = useState(false);
  const [toggleTransactions, setToggleTransactions] = useState(false);
  const [toggleReports, setToggleReports] = useState(false);
  const [newMessage, setNewMessage] =
    // eslint-disable-next-line camelcase
    useState<Wealthlane_NotificationService_DTOs_BellNotificationDto>({});

  const allFieldValues = userAuthList.localization?.values?.Wealthlane;

  const [taskList, setTaskList] = useState(false);
  const [mobileAwardsOpen, setMobileAwardsOpen] = useState(false);
  const [mobileTransactionsOpen, setMobileTransactionsOpen] = useState(false);
  const [mobileReportsOpen, setMobileReportsOpen] = useState(false);
  const [mobileTaskConsoleOpen, setMobileTaskConsoleOpen] = useState(false);
  const [mobileBrokerageOpen, setMobileBrokerageOpen] = useState(false);

  const onSideNavClick = (isClose: boolean): void => {
    const newOpenState = !openSideNav;
    setOpenSideNav(newOpenState);

    // Dispatch custom event to notify app of menu state
    const event = new CustomEvent('mobileMenuToggle', {
      detail: { isOpen: newOpenState },
    });
    window.dispatchEvent(event);

    if (isClose || newOpenState === false) {
      document.getElementsByTagName('html')[0].style.overflow = 'unset';
    } else {
      document.getElementsByTagName('html')[0].style.overflow = 'hidden';
    }
  };

  const filterMyTaskPermissionList = NavPermissionCheck(
    MyTaskRouteList,
    'CustomerService.ApprovalProcess.'
  );

  const filterEquityAwardsList = NavPermissionCheck(EquityAwardsList, 'CustomerService.Customer.');
  const filterTransactionsList = NavPermissionCheck(TransactionsList, 'CustomerService.Customer.');
  const filterTaskManagementConsoleList = NavPermissionCheck(
    TaskManagementConsole,
    'CustomerService.Customer.'
  );
  const filterReportList = NavPermissionCheck(ReportList, 'CustomerService.Customer.');

  const wrapperRef: any = useRef(null);
  useEffect(() => {
    function handleClickOutside(event: Event): void {
      if (
        Boolean(wrapperRef.current) &&
        !(wrapperRef.current as HTMLElement).contains(event.target as Node)
      ) {
        if (openSideNav) {
          // Dispatch custom event to notify app of menu state change
          const menuEvent = new CustomEvent('mobileMenuToggle', {
            detail: { isOpen: false },
          });
          window.dispatchEvent(menuEvent);
        }
        setOpenSideNav(false);
        setToggleAwards(false);
        setTaskList(false);
        setOpen(false);
        setToggleReports(false);
        setToggleTransactions(false);
        setToggleBrokerage(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef, openSideNav]);

  useEffect(() => {
    if (showNotifyFalse) {
      document.body.style.paddingTop = '101px';
    } else {
      document.body.style.paddingTop = '67px';
    }
  }, [showNotifyFalse]);

  // Close mobile menu when screen size changes to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992 && openSideNav) {
        onSideNavClick(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [openSideNav]);

  const storageEventHandler = (): void => {
    dispatch(setNotify({ notify: localStorage.getItem('Notify') === 'Yes' }));
  };

  useEffect(() => {
    dispatch(setNotify({ notify: localStorage.getItem('Notify') === 'Yes' }));
    window.addEventListener('storage', storageEventHandler, false);
  }, []);

  const handleRemoveNotify = (): void => {
    localStorage.setItem('Notify', 'No');
    dispatch(setNotify({ notify: false }));
  };

  useEffect(() => {
    if (isModalOpen) setLoggedIn(false);
  }, [isModalOpen]);

  useEffect(() => {
    const buildAndStartConnection = async (): Promise<any> => {
      const isSignalREnabled = process.env.REACT_APP_ENABLE_SIGNALR_CONNECTION === 'true';

      if (isLoggedIn && isNotify && token && isSignalREnabled) {
        const hubUrl = `${process.env.REACT_APP_WEALTHLANE_API_ENDPOINT ?? ''
          }/api/notification/signalr-hubs/bellNotification`;

        const connection = createSignalRConnection(hubUrl);

        const start = async (): Promise<any> => {
          try {
            await connection.start();
          } catch (err) {
            setTimeout(() => {
              void start();
            }, 5000);
          }
        };

        connection.onclose(() => {
          void start();
        });
        connection.on('ReceiveBellNotification', (user, message) => {
          void refetch();
          setNewMessage(message);
        });
        // Start the connection.
        void start();
      }
    };

    void buildAndStartConnection();
  }, [isLoggedIn]);

  const { handleRequest: handleCustomerRequest, isLoading: isCustomerLoading } =
    useWealthlaneCustomerApi();

  const { data: currentTenantInfo, isLoading: isCurrentTenantInfoLoading } =
    useGetCurrentTenantInfoEnabled(handleCustomerRequest, isAuth);

  const { data: userName } = useGetCustomerUserDetails(handleCustomerRequest, isAuth);

  const { data: companyDetails, isLoading } = useGetCompanyProfileDetails(
    handleCustomerRequest,
    isAuth
  );
  const hasSwitchCompanyPermission = usePermissionCheck({
    type: 'View',
    feature: 'Customer',
    permission: 'SwitchCompany',
  });
  const hasSandboxPermission = usePermissionCheck({
    type: 'View',
    feature: 'Customer',
    permission: 'Sandbox',
  });

  const SandboxInitialValues = {
    data: InitialValuesForm.switchSandboxModal,
    id: '',
  };
  const SwitchInitialValues = {
    data: InitialValuesForm.switchSandboxModal,
    id: '',
  };

  const hasSwitchOrSandbox = hasSwitchCompanyPermission || hasSandboxPermission;

  // const importFairValue = (event: any): void => {
  //   const payload = {
  //     File: event,
  //   };

  //   handleRequest(FairValueService.postApiAwardApiAppFairValueImportFairValue(payload))
  //     .then((data: any) => {
  //       if (data) {
  //         toast.success(<ToastComponent label='Success' message='Fair Value imported.' />);
  //       }
  //     })
  //     .catch((error: any) => {
  //       console.log('error', error);
  //     })
  //     .finally(() => {});
  // };

  // const downloadFairValue = (name: string): void => {
  //   handleRequest(FairValueService.postApiAwardApiAppFairValueDownloadFairValueImportSample())
  //     .then((data: any) => {
  //       if (data != null) {
  //         handleOpenFile(`${name}.xlsx`, data.content);
  //       }
  //     })
  //     .catch((error: any) => {
  //       console.log('error', error);
  //     });
  // };
  const currentHash = window.location.hash;

  const { openFile } = useFilePicker({ accept: '.xlsx, .xls' });

  return (
    <>
      {showNotifyFalse && (
        <HeaderNotice id='headerNotice'>
          <Heading
            variant='body-s'
            title={
              <span className='flex gap-x-2 '>
                Need help? Contact us at
                <a
                  href='mailto:support@wealthlane.co'
                  target='_blank'
                  className='text-blue-600 underline'
                  id='supportEmailLink'>
                  support@wealthlane.co
                </a>
              </span>
            }
            id='headerNoticeText'
          />
          <span
            className='material-symbols-outlined closeIcon'
            onClick={() => {
              handleRemoveNotify();
            }}
            id='headerNoticeClose'>
            close
          </span>
        </HeaderNotice>
      )}
      <NavbarContainerMain
        hasNotify={showNotifyFalse}
        isSwitched={Boolean(isSwitched())}
        isMenuOpen={openSideNav}
        className='nav'
        id='navbarContainer'>
        {/* Don't show any logo during authentication flows (callback, impersonation, etc.) 
            because we can't fetch branding config without proper auth context.
            Also only show logo when branding is enabled and resolved to prevent flickering. */}
        {!isAuthenticatingFlow && isBrandingEnabled && isBrandingResolved && (
          <Link
            className='flex items-center justify-center space-x-4'
            to={`/${currentHash}`}
            id='navLogoLink'>
            {appLogoUrl ? (
              <img className='nav-brand' id='navLogo' src={appLogoUrl} alt='App Logo' />
            ) : (
              <Logo className='nav-brand' id='navLogo' />
            )}
          </Link>
        )}
        {isAuth && Boolean(token) && (
          <>
            <div className='items-center nav-mobile' id='navMobile'>
              <Notification
                NotificationCount={NotificationCount}
                isModalOpen={isModalOpen}
                openModal={() => {
                  openModal();
                }}
                id='mobileNotification'
              />
              <Button
                title='Menu'
                variant='secondary'
                icon='menu'
                onClick={() => {
                  onSideNavClick(false);
                }}
                btnClassName='nav-menu'
                id='mobileMenuButton'
              />

              <MobileMenuBackdrop
                isOpen={openSideNav}
                hasNotify={showNotifyFalse}
                onClick={() => onSideNavClick(true)}
              />
              <NavMenuMobileContainer isOpen={openSideNav} hasNotify={showNotifyFalse}>
                <MobileNavItem className='static navMenuItem'>
                  <div className='nav-close-top-right'>
                    <Button
                      title=''
                      variant='primary-dark'
                      icon='close'
                      onClick={() => {
                        onSideNavClick(true);
                      }}
                      btnClassName='nav-mobile--close'
                    />
                  </div>

                  <Heading variant='h6' title='Menu' className='nav-mobile--heading' />
                  <div className='nav-items'>
                    {/* Overview */}
                    <NavLink
                      to={`/overview${currentHash}`}
                      className={({ isActive }: { isActive: boolean }) =>
                        isActive ? 'active p-2' : ''
                      }
                      onClick={() => {
                        onSideNavClick(true);
                      }}
                      id='mobileOverviewLink'>
                      <Heading variant='body' title='Overview' />
                    </NavLink>
                    {/* Participants */}
                    <CheckPermissions type='View' urlName='participants'>
                      <NavLink
                        to={`/participants${currentHash}`}
                        className={({ isActive }: { isActive: boolean }) =>
                          isActive ? 'active p-2' : ''
                        }
                        onClick={() => {
                          onSideNavClick(true);
                        }}
                        id='mobileParticipantsLink'>
                        <Heading variant='body' title='Participants' />
                      </NavLink>
                    </CheckPermissions>
                    {/* Awards Center */}
                    {filterEquityAwardsList && filterEquityAwardsList?.length > 0 && (
                      <div className='w-full'>
                        <div
                          className='flex items-center justify-between cursor-pointer'
                          onClick={() => setMobileAwardsOpen(!mobileAwardsOpen)}>
                          <Heading variant='body' title='Awards Center' />
                          <span
                            className={`material-symbols-outlined transition-transform ${mobileAwardsOpen ? 'rotate-180' : ''
                              }`}>
                            keyboard_arrow_down
                          </span>
                        </div>
                        <MobileSubMenu className={mobileAwardsOpen ? 'open' : ''}>
                          {filterEquityAwardsList?.map(
                            (
                              item: {
                                url: string;
                                permissionName: string | [string];
                                name: string;
                              },
                              index: number
                            ) => (
                              <NavLink
                                key={`mobile-${item.permissionName}-${item.url}-${index}`}
                                to={item.url}
                                onClick={() => {
                                  onSideNavClick(true);
                                }}
                                className={({ isActive }: { isActive: boolean }) =>
                                  isActive ? 'active block' : 'block'
                                }>
                                <Heading variant='body-s' title={item.name} />
                              </NavLink>
                            )
                          )}
                        </MobileSubMenu>
                      </div>
                    )}

                    {/* Reports */}
                    {filterReportList && filterReportList?.length > 0 && (
                      <div className='w-full'>
                        <div
                          className='flex items-center justify-between cursor-pointer'
                          onClick={() => setMobileReportsOpen(!mobileReportsOpen)}>
                          <Heading variant='body' title='Reports' />
                          <span
                            className={`material-symbols-outlined transition-transform ${mobileReportsOpen ? 'rotate-180' : ''
                              }`}>
                            keyboard_arrow_down
                          </span>
                        </div>
                        <MobileSubMenu className={mobileReportsOpen ? 'open' : ''}>
                          {filterReportList?.map(
                            (item: { url: string; permissionName: string; name: string }) => (
                              <NavLink
                                key={item.url}
                                to={item.url}
                                onClick={() => {
                                  onSideNavClick(true);
                                }}
                                className={({ isActive }: { isActive: boolean }) =>
                                  isActive ? 'active block' : 'block'
                                }>
                                <Heading variant='body-s' title={item.name} />
                              </NavLink>
                            )
                          )}
                        </MobileSubMenu>
                      </div>
                    )}

                    {/* Task Management */}
                    {filterTaskManagementConsoleList &&
                      filterTaskManagementConsoleList?.length > 0 && (
                        <div className='w-full'>
                          <div
                            className='flex items-center justify-between cursor-pointer'
                            onClick={() => setMobileTaskConsoleOpen(!mobileTaskConsoleOpen)}>
                            <Heading variant='body' title='Task Management' />
                            <span
                              className={`material-symbols-outlined transition-transform ${mobileTaskConsoleOpen ? 'rotate-180' : ''
                                }`}>
                              keyboard_arrow_down
                            </span>
                          </div>
                          <MobileSubMenu className={mobileTaskConsoleOpen ? 'open' : ''}>
                            {filterTaskManagementConsoleList?.map(
                              (item: { url: string; permissionName: string; name: string }) => (
                                <NavLink
                                  key={item.url}
                                  to={item.url}
                                  onClick={() => {
                                    onSideNavClick(true);
                                  }}
                                  className={({ isActive }: { isActive: boolean }) =>
                                    isActive ? 'active block' : 'block'
                                  }>
                                  <Heading variant='body-s' title={item.name} />
                                </NavLink>
                              )
                            )}
                          </MobileSubMenu>
                        </div>
                      )}

                    {/* Transactions */}
                    {filterTransactionsList && filterTransactionsList?.length > 0 && (
                      <div className='w-full'>
                        <div
                          className='flex items-center justify-between cursor-pointer'
                          onClick={() => setMobileTransactionsOpen(!mobileTransactionsOpen)}>
                          <Heading variant='body' title='Transactions' />
                          <span
                            className={`material-symbols-outlined transition-transform ${mobileTransactionsOpen ? 'rotate-180' : ''
                              }`}>
                            keyboard_arrow_down
                          </span>
                        </div>
                        <MobileSubMenu className={mobileTransactionsOpen ? 'open' : ''}>
                          {filterTransactionsList?.map(
                            (item: { url: string; permissionName: string; name: string }) => (
                              <NavLink
                                key={item.url}
                                to={item.url}
                                onClick={() => {
                                  onSideNavClick(true);
                                }}
                                className={({ isActive }: { isActive: boolean }) =>
                                  isActive ? 'active block' : 'block'
                                }>
                                <Heading variant='body-s' title={item.name} />
                              </NavLink>
                            )
                          )}
                        </MobileSubMenu>
                      </div>
                    )}

                    {/* Customers */}
                    <CheckPermissions type='View' urlName='customer-list'>
                      <NavLink
                        to={`/customer-list${currentHash}`}
                        className={({ isActive }: { isActive: boolean }) =>
                          isActive ? 'active p-2' : ''
                        }
                        onClick={() => {
                          onSideNavClick(true);
                        }}
                        id='mobileCustomersLink'>
                        <Heading variant='body' title='Customers' />
                      </NavLink>
                    </CheckPermissions>

                    {/* Brokerage Import & Export */}
                    <CheckPermissions type='View' urlName='brokerage-import-export'>
                      <div className='w-full'>
                        <div
                          className='flex items-center justify-between cursor-pointer'
                          onClick={() => setMobileBrokerageOpen(!mobileBrokerageOpen)}>
                          <Heading variant='body' title='Brokerage' />
                          <span
                            className={`material-symbols-outlined transition-transform ${mobileBrokerageOpen ? 'rotate-180' : ''
                              }`}>
                            keyboard_arrow_down
                          </span>
                        </div>
                        <MobileSubMenu className={mobileBrokerageOpen ? 'open' : ''}>
                          <NavLink
                            to='/brokerage-import-export'
                            onClick={() => {
                              onSideNavClick(true);
                            }}
                            className={({ isActive }: { isActive: boolean }) =>
                              isActive ? 'active block' : 'block'
                            }>
                            <Heading variant='body-s' title='Import & Export' />
                          </NavLink>
                          <NavLink
                            to='/dashboard/brokerage-account'
                            onClick={() => {
                              onSideNavClick(true);
                            }}
                            className={({ isActive }: { isActive: boolean }) =>
                              isActive ? 'active block' : 'block'
                            }>
                            <Heading variant='body-s' title='Brokerage Account' />
                          </NavLink>
                          <NavLink
                            to='/dashboard/trade-order'
                            onClick={() => {
                              onSideNavClick(true);
                            }}
                            className={({ isActive }: { isActive: boolean }) =>
                              isActive ? 'active block' : 'block'
                            }>
                            <Heading variant='body-s' title='Trade Order' />
                          </NavLink>
                          <NavLink
                            to='/dashboard/transfer-and-allocate'
                            onClick={() => {
                              onSideNavClick(true);
                            }}
                            className={({ isActive }: { isActive: boolean }) =>
                              isActive ? 'active block' : 'block'
                            }>
                            <Heading variant='body-s' title='Transfer and Allocate' />
                          </NavLink>
                          <NavLink
                            to='/dashboard/drs-registration'
                            onClick={() => {
                              onSideNavClick(true);
                            }}
                            className={({ isActive }: { isActive: boolean }) =>
                              isActive ? 'active block' : 'block'
                            }>
                            <Heading variant='body-s' title='DRS Registration' />
                          </NavLink>
                          <NavLink
                            to='/dashboard/bank-account'
                            onClick={() => {
                              onSideNavClick(true);
                            }}
                            className={({ isActive }: { isActive: boolean }) =>
                              isActive ? 'active block' : 'block'
                            }>
                            <Heading variant='body-s' title='Bank Account' />
                          </NavLink>
                          <NavLink
                            to='/dashboard/cash-transfer'
                            onClick={() => {
                              onSideNavClick(true);
                            }}
                            className={({ isActive }: { isActive: boolean }) =>
                              isActive ? 'active block' : 'block'
                            }>
                            <Heading variant='body-s' title='Cash Transfer' />
                          </NavLink>
                          <NavLink
                            to='/dashboard/disbursement'
                            onClick={() => {
                              onSideNavClick(true);
                            }}
                            className={({ isActive }: { isActive: boolean }) =>
                              isActive ? 'active block' : 'block'
                            }>
                            <Heading variant='body-s' title='Disbursement' />
                          </NavLink>
                        </MobileSubMenu>
                      </div>
                    </CheckPermissions>
                    <CheckPermissions type='View' urlName='transfer-agent'>
                      <NavLink
                        to={`/transfer-agent`}
                        className={({ isActive }: { isActive: boolean }) =>
                          isActive ? 'active p-2' : ''
                        }
                        onClick={() => {
                          onSideNavClick(true);
                        }}>
                        <Heading variant='body' title='Transfer Agent' />
                      </NavLink>
                    </CheckPermissions>
                    {/* Settings */}
                    <NavLink
                      to={`/settings/organization-settings/company-profile${currentHash}`}
                      className={({ isActive }: { isActive: boolean }) =>
                        isActive ? 'active p-2' : ''
                      }
                      onClick={() => {
                        onSideNavClick(true);
                      }}
                      id='mobileSettingsLink'>
                      <Heading variant='body' title='Settings' />
                    </NavLink>
                  </div>
                </MobileNavItem>

                <MobileNavItem className='static navMenuItem'>
                  <Heading
                    variant='h6'
                    title={userAuthList?.currentUser?.name}
                    className='nav-mobile--heading'
                    id='mobileUserName'
                  />
                  {!isLoading && (
                    <div className='flex items-center justify-start cursor-pointer mb-4 '>
                      <NavLogo
                        data={companyDetails}
                        handleRequest={handleCustomerRequest}
                        onSwitchModal={() => hasSwitchOrSandbox && openSwitchModal()}
                      />
                    </div>
                  )}
                  <div className='nav-items' id='mobileUserActions'>
                    <Heading
                      variant='body'
                      title='Log Out'
                      onClick={() => {
                        void authService.logout();
                      }}
                      className='cursor-pointer'
                      id='mobileLogoutButton'
                    />
                    <div className='nav-close' id='mobileNavClose'>
                      <Button
                        title='Close'
                        variant='primary-dark'
                        icon='close'
                        onClick={() => {
                          onSideNavClick(true);
                        }}
                        btnClassName='nav-mobile--close'
                        id='mobileCloseButton'
                      />
                    </div>
                    <div className='nav-close nav-close-top-right' id='mobileNavCloseTopRight'>
                      <Button
                        title=''
                        variant='primary-dark'
                        icon='close'
                        onClick={() => {
                          onSideNavClick(true);
                        }}
                        btnClassName='nav-mobile--close'
                        id='mobileCloseTopRightButton'
                      />
                    </div>
                  </div>
                </MobileNavItem>
              </NavMenuMobileContainer>
            </div>
            <NavMenuContainer className='relative nav-main' id='desktopNavContainer'>
              {/* <CheckPermissions type='View' urlName='overview'> */}
              <NavMenuCon className='static navMenuItem' id='overviewNavItem'>
                <NavLink
                  to={`/overview${currentHash}`}
                  className={({ isActive }: { isActive: boolean }) => (isActive ? 'active' : '')}
                  id='overviewLink'>
                  Overview
                </NavLink>
              </NavMenuCon>
              {/* </CheckPermissions> */}
              <CheckPermissions type='View' urlName='participants'>
                <NavMenuCon className='static navMenuItem' id='participantsNavItem'>
                  <NavLink
                    to={`/participants${currentHash}`}
                    className={({ isActive }: { isActive: boolean }) => (isActive ? 'active' : '')}
                    id='participantsLink'>
                    Participants
                  </NavLink>
                </NavMenuCon>
              </CheckPermissions>
              {filterEquityAwardsList != null && filterEquityAwardsList.length > 0 && (
                <div
                  className='relative flex items-center gap-x-2'
                  onClick={() => {
                    setToggleAwards(!toggleAwards);
                  }}
                  id='awardsDropdownContainer'>
                  <NavMenuCon className='static navMenuItem' id='awardsCenterNavItem'>
                    <NavLink
                      to={`/awards${currentHash}`}
                      className={({ isActive }) =>
                        isActive ? 'active pointer-events-none' : 'pointer-events-none'
                      }
                      id='awardsCenterLink'>
                      Awards Center
                    </NavLink>
                  </NavMenuCon>
                  {toggleAwards && (
                    <ProfileDropdownMenu
                      ref={wrapperRef}
                      className='nav-subMenu'
                      id='awardsDropdownMenu'>
                      {filterEquityAwardsList.map(
                        (
                          item: { url: string; permissionName: string; name: string },
                          index: number
                        ) => (
                          <DropDownMenuAdmin
                            key={`desktop-award-${index}`}
                            id={`${toCamelCase(item.name)}DropdownItem`}>
                            <NavLink
                              to={`${item.url}${currentHash}`}
                              onClick={() => {
                                setToggleAwards(!toggleAwards);
                              }}
                              id={`${toCamelCase(item.name)}DropdownLink`}>
                              <h1 className='para' role='button'>
                                {item.name}
                              </h1>
                            </NavLink>
                          </DropDownMenuAdmin>
                        )
                      )}
                    </ProfileDropdownMenu>
                  )}
                </div>
              )}

              {filterReportList?.map(
                (item: { url: string; permissionName: string; name: string }, index: number) => (
                  <NavMenuCon
                    className='static navMenuItem'
                    key={`desktop-report-nav-${index}`}
                    id={`${toCamelCase(item.name)}NavItem`}>
                    <NavLink
                      to={`${item?.url}${currentHash}`}
                      className={({ isActive }: { isActive: boolean }) =>
                        isActive ? 'active' : ''
                      }
                      id={`${toCamelCase(item.name)}Link`}>
                      <Heading variant='body' title={item.name} />
                    </NavLink>
                  </NavMenuCon>
                )
              )}
              {/* <div                className='relative flex items-center gap-x-2'                onClick={() => {                  setToggleReports(!toggleReports);                }}>                <NavMenuCon className='static navMenuItem'>                  <NavLink                    to={`/reports${currentHash}`}                    className={({ isActive }) =>                      isActive ? 'active pointer-events-none' : 'pointer-events-none'                    }>                    Reporting                  </NavLink>                </NavMenuCon>                {toggleReports && (                  <ProfileDropdownMenu ref={wrapperRef} className='nav-subMenu'>                    {REPORTS_LIST.map((item) => (                      <DropDownMenuAdmin>                        <NavLink                          to={`${item.url}${currentHash}`}                          onClick={() => {                            setToggleReports(!toggleReports);                          }}>                          <h1 className='para' role='button'>                            {item.name}                          </h1>                        </NavLink>                      </DropDownMenuAdmin>                    ))}                  </ProfileDropdownMenu>                )}              </div> */}
              {filterTaskManagementConsoleList?.map(
                (item: { url: string; permissionName: string; name: string }, index: number) => (
                  <NavMenuCon
                    className='static navMenuItem'
                    key={`desktop-task-nav-${index}`}
                    id={`${toCamelCase(item.name)}NavItem`}>
                    <NavLink
                      to={`${item?.url}${currentHash}`}
                      className={({ isActive }: { isActive: boolean }) =>
                        isActive ? 'active' : ''
                      }
                      id={`${toCamelCase(item.name)}Link`}>
                      <Heading variant='body' title={item.name} />
                    </NavLink>
                  </NavMenuCon>
                )
              )}
              {currentTenant?.tenantType === 'Main' && (
                <CheckPermissions type='View' urlName='customer-list'>
                  <NavMenuCon className='static navMenuItem' id='customersNavItem'>
                    <NavLink
                      to={`/customer-list${currentHash}`}
                      className={({ isActive }: { isActive: boolean }) =>
                        isActive ? 'active' : ''
                      }
                      id='customersLink'>
                      Customers
                    </NavLink>
                  </NavMenuCon>
                </CheckPermissions>
              )}
              {filterTransactionsList != null && filterTransactionsList.length > 0 && (
                <div
                  className='relative flex items-center gap-x-2'
                  onClick={() => {
                    setToggleTransactions(!toggleTransactions);
                  }}
                  id='transactionsDropdownContainer'>
                  <NavMenuCon className='static navMenuItem' id='transactionsNavItem'>
                    <NavLink
                      to={`/transactions${currentHash}`}
                      className={({ isActive }) =>
                        isActive ? 'active pointer-events-none' : 'pointer-events-none'
                      }
                      id='transactionsLink'>
                      Transactions
                    </NavLink>
                  </NavMenuCon>
                  {toggleTransactions && (
                    <ProfileDropdownMenu
                      ref={wrapperRef}
                      className='nav-subMenu'
                      id='transactionsDropdownMenu'>
                      {filterTransactionsList.map(
                        (
                          item: { url: string; permissionName: string; name: string },
                          index: number
                        ) => (
                          <DropDownMenuAdmin
                            key={`desktop-transaction-${index}`}
                            id={`${toCamelCase(item.name)}DropdownItem`}>
                            <NavLink
                              to={`${item.url}${currentHash}`}
                              onClick={() => {
                                setToggleTransactions(!toggleTransactions);
                              }}
                              id={`${toCamelCase(item.name)}DropdownLink`}>
                              <h1 className='para' role='button'>
                                {item.name}
                              </h1>
                            </NavLink>
                          </DropDownMenuAdmin>
                        )
                      )}
                    </ProfileDropdownMenu>
                  )}
                </div>
              )}
              {currentTenant?.tenantType === 'Main' && (
                <div
                  className='relative flex items-center gap-x-2'
                  onClick={() => {
                    setToggleBrokerage(!toggleBrokerage);
                  }}
                  id='brokerageDropdownContainer'>
                  <CheckPermissions type='View' urlName='brokerage-import-export'>
                    <NavMenuCon className='static navMenuItem' id='brokerageNavItem'>
                      <NavLink
                        to={`/dashboard${currentHash}`}
                        className={({ isActive }) =>
                          isActive ? 'active pointer-events-none' : 'pointer-events-none'
                        }
                        id='brokerageLink'>
                        Brokerage
                      </NavLink>
                    </NavMenuCon>
                    {toggleBrokerage && (
                      <ProfileDropdownMenu
                        ref={wrapperRef}
                        className='nav-subMenu'
                        id='brokerageDropdownMenu'>
                        <DropDownMenuAdmin className='' id='brokerageImportDropdownItem'>
                          <NavLink
                            to={`/brokerage-import-export${currentHash}`}
                            className={({ isActive }: { isActive: boolean }) =>
                              isActive ? 'active' : ''
                            }
                            id='brokerageImportDropdownLink'>
                            Import & Export
                          </NavLink>
                        </DropDownMenuAdmin>
                        <DropDownMenuAdmin id='brokerageAccountDropdownItem'>
                          <NavLink
                            to={`/dashboard/brokerage-account${currentHash}`}
                            className={({ isActive }: { isActive: boolean }) =>
                              isActive ? 'active' : ''
                            }
                            id='brokerageAccountDropdownLink'>
                            Brokerage Account
                          </NavLink>
                        </DropDownMenuAdmin>
                        <DropDownMenuAdmin id='tradeOrderDropdownItem'>
                          <NavLink
                            to={`/dashboard/trade-order${currentHash}`}
                            className={({ isActive }: { isActive: boolean }) =>
                              isActive ? 'active' : ''
                            }
                            id='tradeOrderDropdownLink'>
                            Trade Order
                          </NavLink>
                        </DropDownMenuAdmin>
                        <DropDownMenuAdmin id='transferAllocateDropdownItem'>
                          <NavLink
                            to={`/dashboard/transfer-and-allocate${currentHash}`}
                            className={({ isActive }: { isActive: boolean }) =>
                              isActive ? 'active' : ''
                            }
                            id='transferAllocateDropdownLink'>
                            Transfer and Allocate
                          </NavLink>
                        </DropDownMenuAdmin>
                        <DropDownMenuAdmin id='drsRegistrationDropdownItem'>
                          <NavLink
                            to={`/dashboard/drs-registration${currentHash}`}
                            className={({ isActive }: { isActive: boolean }) =>
                              isActive ? 'active' : ''
                            }
                            id='drsRegistrationDropdownLink'>
                            DRS Registration
                          </NavLink>
                        </DropDownMenuAdmin>
                        <DropDownMenuAdmin id='bankAccountDropdownItem'>
                          <NavLink
                            to={`/dashboard/bank-account${currentHash}`}
                            className={({ isActive }: { isActive: boolean }) =>
                              isActive ? 'active' : ''
                            }
                            id='bankAccountDropdownLink'>
                            Bank Account
                          </NavLink>
                        </DropDownMenuAdmin>
                        <DropDownMenuAdmin id='cashTransferDropdownItem'>
                          <NavLink
                            to={`/dashboard/cash-transfer${currentHash}`}
                            className={({ isActive }: { isActive: boolean }) =>
                              isActive ? 'active' : ''
                            }
                            id='cashTransferDropdownLink'>
                            Cash Transfer
                          </NavLink>
                        </DropDownMenuAdmin>
                        <DropDownMenuAdmin id='disbursementDropdownItem'>
                          <NavLink
                            to='/dashboard/disbursement'
                            onClick={() => {
                              onSideNavClick(true);
                            }}
                            className={({ isActive }: { isActive: boolean }) =>
                              isActive ? 'active' : ''
                            }
                            id='disbursementDropdownLink'>
                            Disbursement
                          </NavLink>
                        </DropDownMenuAdmin>
                      </ProfileDropdownMenu>
                    )}
                  </CheckPermissions>
                </div>
              )}
              {currentTenant?.tenantType === 'Main' && (
                <CheckPermissions type='View' urlName='transfer-agent'>
                  <NavMenuCon className='static navMenuItem'>
                    <NavLink
                      to={'/transfer-agent'}
                      className={({ isActive }) => (isActive ? 'active' : '')}>
                      Transfer Agent
                    </NavLink>
                  </NavMenuCon>
                </CheckPermissions>
              )}
              <NavMenuCon className='static navMenuItem'>
                <NavLink
                  to={`/settings${currentHash}`}
                  className={({ isActive }: { isActive: boolean }) => (isActive ? 'active' : '')}>
                  Settings
                </NavLink>
              </NavMenuCon>

              <Spacer width={5} />
              <Notification
                NotificationCount={NotificationCount}
                isModalOpen={isModalOpen}
                openModal={() => {
                  openModal();
                }}
                id='desktopNotification'
              />
            </NavMenuContainer>
            <ProfileDropdownContainer className='nav-main' id='profileDropdownContainer'>
              {/* {filterMyTaskPermissionList != null && filterMyTaskPermissionList.length > 0 && (
                <div className='flex items-center gap-x-3 border-r-[3px] relative'>
                  <NavMenuCon
                    className='static navMenuItem'
                    onClick={() => {
                      setTaskList(!taskList);
                    }}>
                    <Badge icon='task_alt' variant='primary' title='My Tasks' />
                  </NavMenuCon>
                  {taskList && (
                    <TaskSubMenuDropDown ref={wrapperRef}>
                      {filterMyTaskPermissionList.map((item: any) => {
                        const findKey =
                          allFieldValues != null &&
                          Object.keys(allFieldValues).find((keyVal) =>
                            keyVal.includes(item.permissionName)
                          );
                        return (
                          <DropDownMenuAdmin>
                            <NavLink
                              to={`${item.url}${currentHash}`}
                              onClick={() => {
                                setTaskList(!taskList);
                              }}
                              className='flex justify-start w-full'>
                              {findKey != null && findKey !== false && allFieldValues != null
                                ? Boolean(allFieldValues[findKey]) && item?.name
                                : '-'}
                            </NavLink>
                          </DropDownMenuAdmin>
                        );
                      })}
                    </TaskSubMenuDropDown>
                  )}
                </div>
              )} */}
              {!isLoading && (
                <div
                  className='flex items-center justify-center cursor-pointer'
                  id='desktopNavLogo'>
                  <NavLogo
                    data={companyDetails}
                    handleRequest={handleCustomerRequest}
                    onSwitchModal={() => hasSwitchOrSandbox && openSwitchModal()}
                  />
                </div>
              )}
              <Spacer width={5} />
              <ProfileDropWrapper id='profileDropWrapper'>
                <ProfileDrop
                  onClick={() => {
                    setOpen(!open);
                  }}
                  id='profileDropButton'>
                  <div className='font-semibold title' id='profileUserName'>
                    {userName?.firstName != null
                      ? String(userName?.firstName ?? '') + ' ' + String(userName?.lastName ?? '')
                      : ''}
                  </div>
                  <span id='profileDropArrow'>
                    <span className='material-symbols-rounded downIcon'>arrow_drop_down</span>
                  </span>
                </ProfileDrop>
                {open && (
                  <ProfileDropdownMenu ref={wrapperRef} id='profileDropdownMenu'>
                    <Link
                      to={`/profile${currentHash}`}
                      onClick={() => {
                        setOpen(false);
                      }}
                      id='profileLink'>
                      <DropDownMenuSuperAdmin>My Profile</DropDownMenuSuperAdmin>
                    </Link>
                    {hasSwitchOrSandbox && (
                      <span
                        onClick={() => {
                          setOpen(false);
                          openSwitchModal();
                        }}
                        id='switchInstanceButton'>
                        <DropDownMenuSuperAdmin>Switch Instance</DropDownMenuSuperAdmin>
                      </span>
                    )}
                    <CheckPermissions type='View' urlName='notification'>
                      <Link
                        to={`/notification${currentHash}`}
                        onClick={() => {
                          setOpen(false);
                        }}
                        id='notificationLink'>
                        <DropDownMenuSuperAdmin>Notification</DropDownMenuSuperAdmin>
                      </Link>
                    </CheckPermissions>
                    {/*                     <div                      onClick={() => {                        setSubMenu(!subMenu);                      }}>                      <DropDownMenuSuperAdmin>My Sandbox</DropDownMenuSuperAdmin>                    </div>                    {subMenu && (                      <div className='sub-menu-container'>                        <div                          onClick={() => {                            openSandboxModal();                            setOpen(false);                          }}>                          <DropDownMenuSuperAdmin>Add Sandbox</DropDownMenuSuperAdmin>                        </div>                        <Link                          to={`/notification${currentHash}`}                          onClick={() => {                            setOpen(false);                          }}>                          <DropDownMenuSuperAdmin>SB 1</DropDownMenuSuperAdmin>                        </Link>                        <Link                          to={`/notification${currentHash}`}                          onClick={() => {                            setOpen(false);                          }}>                          <DropDownMenuSuperAdmin>SB 2</DropDownMenuSuperAdmin>                        </Link>                      </div>                    )} */}
                    {/* <Link                      to={`/notification${currentHash}`}                      onClick={() => {                        setOpen(false);                      }}>                      <DropDownMenuSuperAdmin>Notification</DropDownMenuSuperAdmin>                    </Link> */}
                    <Link
                      to={`/security${currentHash}`}
                      onClick={() => {
                        setOpen(false);
                      }}
                      id='securityLink'>
                      <DropDownMenuSuperAdmin>Security</DropDownMenuSuperAdmin>
                    </Link>
                    <DropDownMenuSuperAdmin
                      className='logoutBtn'
                      onClick={() => {
                        void authService.logout();
                        localStorage.removeItem('reportType');
                      }}
                      id='logoutButton'>
                      <h1 className='para' role='button'>
                        Logout
                      </h1>
                    </DropDownMenuSuperAdmin>
                  </ProfileDropdownMenu>
                )}
              </ProfileDropWrapper>
            </ProfileDropdownContainer>
          </>
        )}
      </NavbarContainerMain>
      <AnimationWrapper id='notificationModalWrapper'>
        {isModalOpen && (
          <NotificationModal
            handleRequest={handleRequest}
            isModalOpen={isModalOpen}
            newMessage={newMessage}
            closeModal={closeModal}
            domId='notificationModal'
          />
        )}
      </AnimationWrapper>
      <AnimationWrapper id='sandboxModalWrapper'>
        {isSandboxModalOpen && (
          <SwitchSandboxModal
            isModalOpen={isSandboxModalOpen}
            closeModal={closeSandboxModal}
            modalStatus={'Add'}
            // modalLoading={modalLoading}
            initialData={SandboxInitialValues}
            setModalStatus={() => 'Add'}
            domId='navbarSandboxModal'
          // rowData={rowData}
          />
        )}
      </AnimationWrapper>
      {createPortal(
        <AnimationWrapper>
          {isSwitchModalOpen && (
            <NavbarSwitchModal
              isModalOpen={isSwitchModalOpen}
              closeModal={closeSwitchModal}
              modalStatus={'Add'}
              // modalLoading={modalLoading}
              initialData={SwitchInitialValues}
              setModalStatus={() => 'Add'}
            // rowData={rowData}
            />
          )}
        </AnimationWrapper>,
        document.getElementsByTagName('html')[0]
      )}
      {<AnimationWrapper>
        {currentTenantInfo && userName && (
          <div className='relative'>

            <ChatbotWithContext
              axiosInstance={axiosInstance}
              config={configState}
              tenantIdAccessor={() => currentTenantInfo?.id}
              langfusePublicKey={process.env.REACT_APP_LANGFUSE_PUBLIC_KEY!}
              langfuseEndpointUrl={process.env.REACT_APP_LANGFUSE_BASE_URL!}
              accessTokenAccessor={() => authService.getToken()}
              customerName={getUserName(userName)}
              sourceRoleId={getRoleId()}
              httpUrl={process.env.REACT_APP_CHATBOT_REST_URL!}
              wsUrl={process.env.REACT_APP_CHATBOT_WEBSOCKET_API!}
              alertFunctionSuccess={(message: string) => {
                toast.success(<ToastComponent label='Success' message={message} />);
              }}
              alertFunctionError={(message: string) => {
                toast.error(<ToastComponent label='Error' message={message} />);
              }}
              navigate={navigate}
              allowMention={false}
                cid={cid.toString() ?? ''}
            />

          </div>
        )}
      </AnimationWrapper>}
    </>
  );
};

export default Navbar;
